import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, CalendarDays, CheckCircle2, XCircle, Clock, BookOpen, ClipboardCheck, ArrowLeft, Download, MessageSquare, Edit, Image, Send, ChevronLeft, ChevronRight, X, Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILES_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/files`;

const statusColors = {
  present: 'bg-success/15 text-success-foreground',
  absent: 'bg-destructive/15 text-destructive',
  late: 'bg-accent/15 text-accent-foreground',
};

const RatingStars = ({ value }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className={`w-4 h-4 ${n <= value ? 'text-accent fill-accent' : 'text-border'}`} />
    ))}
  </div>
);

const sectionTabs = [
  { id: 'progress', label: 'Progress', icon: BookOpen },
  { id: 'attendance', label: 'Attendance', icon: ClipboardCheck },
  { id: 'photos', label: 'Photos', icon: Image },
  { id: 'chat', label: 'Chat', icon: MessageSquare },
  { id: 'info', label: 'Update', icon: Edit },
];

const MobileParent = () => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [activeSection, setActiveSection] = useState('progress');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [sendingChat, setSendingChat] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const chatEndRef = useRef(null);
  const photoInputRef = useRef(null);
  const [editInfo, setEditInfo] = useState({});
  const [savingInfo, setSavingInfo] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    setError('');
    if (value && index < 5) {
      document.getElementById(`m-code-${index + 1}`)?.focus();
    }
    if (newCode.every((d) => d !== '') && index === 5) {
      fetchData(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      document.getElementById(`m-code-${index - 1}`)?.focus();
    }
  };

  const fetchData = async (codeStr) => {
    setLoading(true);
    setError('');
    try {
      const { data: result } = await axios.post(`${API}/parent/access`, { access_code: codeStr });
      setData(result);
      setAccessCode(codeStr);
      setChatMessages(result.chat_messages || []);
      setEditInfo({ phone: result.child.parent_phone || '', address: result.child.address || '', emergency_contact_name: result.child.emergency_contacts?.[0]?.name || '', emergency_contact_phone: result.child.emergency_contacts?.[0]?.phone || '' });
    } catch {
      setError('Invalid access code. Please try again.');
      setCode(['', '', '', '', '', '']);
      document.getElementById('m-code-0')?.focus();
    } finally {
      setLoading(false);
    }
  };

  const sendChat = async () => {
    if (!chatInput.trim()) return;
    setSendingChat(true);
    try {
      const { data: msg } = await axios.post(`${API}/parent/chat?access_code=${accessCode}`, { message: chatInput });
      setChatMessages(prev => [...prev, msg]);
      setChatInput('');
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch { toast.error('Failed to send message'); }
    finally { setSendingChat(false); }
  };

  const refreshChat = async () => {
    try {
      const { data: msgs } = await axios.get(`${API}/parent/chat?access_code=${accessCode}`);
      setChatMessages(msgs);
    } catch {}
  };

  const sendPhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data: msg } = await axios.post(`${API}/parent/chat/photo?access_code=${accessCode}`, formData);
      setChatMessages(prev => [...prev, msg]);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch { toast.error('Failed to send photo'); }
    finally { setUploadingPhoto(false); }
  };

  const fetchUnread = async () => {
    if (!accessCode) return;
    try {
      const { data } = await axios.get(`${API}/parent/chat/unread?access_code=${accessCode}`);
      setUnreadCount(data.unread);
    } catch {}
  };

  const markAsRead = async () => {
    try {
      await axios.post(`${API}/parent/chat/read?access_code=${accessCode}`);
      setUnreadCount(0);
    } catch {}
  };

  // Poll for new messages when chat tab is active
  useEffect(() => {
    if (activeSection !== 'chat' || !accessCode) return;
    markAsRead();
    const interval = setInterval(refreshChat, 5000);
    return () => clearInterval(interval);
  }, [activeSection, accessCode]);

  // Poll for unread count when NOT on chat tab
  useEffect(() => {
    if (!accessCode || activeSection === 'chat') return;
    fetchUnread();
    const interval = setInterval(fetchUnread, 8000);
    return () => clearInterval(interval);
  }, [accessCode, activeSection]);

  const saveInfo = async () => {
    setSavingInfo(true);
    try {
      await axios.put(`${API}/parent/info`, { access_code: accessCode, updates: editInfo });
      toast.success('Info updated!');
    } catch { toast.error('Failed to update'); }
    finally { setSavingInfo(false); }
  };

  const exportProgressPDF = () => {
    if (!data?.progress?.length) return;
    const doc = new jsPDF();
    doc.setFontSize(18); doc.text(`${data.child.name} - Progress Report`, 14, 20);
    autoTable(doc, {
      startY: 30,
      head: [['Date', 'Area', 'Rating', 'Notes']],
      body: data.progress.map(r => [new Date(r.date).toLocaleDateString(), r.area, `${r.rating}/5`, r.notes || '']),
    });
    doc.save(`${data.child.name}_progress.pdf`);
  };

  const exportAttendancePDF = () => {
    if (!data?.attendance?.length) return;
    const doc = new jsPDF();
    doc.setFontSize(18); doc.text(`${data.child.name} - Attendance`, 14, 20);
    autoTable(doc, {
      startY: 30,
      head: [['Date', 'Status', 'Notes']],
      body: data.attendance.map(a => [new Date(a.date).toLocaleDateString(), a.status, a.notes || '']),
    });
    doc.save(`${data.child.name}_attendance.pdf`);
  };

  const logout = () => { setData(null); setCode(['', '', '', '', '', '']); setAccessCode(''); };

  // Login Screen
  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] px-6" data-testid="mobile-parent-login">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="bg-white rounded-3xl shadow-elevated border border-border/30 p-8 space-y-6">
            <div className="text-center space-y-2">
              <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center mx-auto">
                <BookOpen className="w-7 h-7 text-secondary" />
              </div>
              <h2 className="font-fredoka font-bold text-xl text-foreground" data-testid="mobile-parent-heading">Parent Portal</h2>
              <p className="text-sm text-muted-foreground">Enter your 6-digit access code</p>
            </div>

            <div className="flex justify-center gap-2">
              {code.map((digit, i) => (
                <input
                  key={i} id={`m-code-${i}`} type="tel" inputMode="numeric" maxLength={1}
                  value={digit} onChange={(e) => handleChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)} disabled={loading}
                  className="w-11 h-13 text-center text-xl font-bold rounded-xl border-2 border-border/60 focus:border-secondary focus:outline-none focus:ring-2 focus:ring-secondary/20 transition-all bg-muted/30"
                  data-testid={`mobile-parent-code-${i}`}
                />
              ))}
            </div>

            {error && <p className="text-center text-destructive text-sm font-medium" data-testid="mobile-parent-error">{error}</p>}
            {loading && <p className="text-center text-muted-foreground text-sm">Verifying...</p>}
          </div>
        </motion.div>
      </div>
    );
  }

  // Dashboard
  const child = data.child;
  const photos = data.photos || [];

  return (
    <div className="pb-4" data-testid="mobile-parent-dashboard">
      {/* Header */}
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-fredoka font-bold text-xl text-foreground" data-testid="mobile-parent-child-name">{child.name}</h2>
            <p className="text-xs text-muted-foreground">Age {child.age} {child.nickname ? `"${child.nickname}"` : ''}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} className="text-xs text-muted-foreground" data-testid="mobile-parent-logout">
            <ArrowLeft className="w-4 h-4 mr-1" /> Logout
          </Button>
        </div>
      </div>

      {/* Section Tabs - horizontally scrollable */}
      <div className="px-4 mb-4 overflow-x-auto no-scrollbar">
        <div className="flex gap-2 w-max">
          {sectionTabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id} onClick={() => { setActiveSection(id); if (id === 'chat') markAsRead(); }}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap relative ${activeSection === id ? 'bg-secondary text-white shadow-soft' : 'bg-white text-muted-foreground border border-border/40'}`}
              data-testid={`mobile-parent-tab-${id}`}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
              {id === 'chat' && unreadCount > 0 && activeSection !== 'chat' && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-destructive text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="chat-unread-dot">{unreadCount}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-4">
        <AnimatePresence mode="wait">
          <motion.div key={activeSection} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.2 }}>

            {/* Progress */}
            {activeSection === 'progress' && (
              <div className="space-y-3" data-testid="mobile-parent-progress">
                <div className="flex items-center justify-between">
                  <h3 className="font-fredoka font-semibold text-foreground">Progress Reports</h3>
                  {data.progress?.length > 0 && (
                    <Button variant="ghost" size="sm" onClick={exportProgressPDF} className="text-xs" data-testid="mobile-export-progress">
                      <Download className="w-3.5 h-3.5 mr-1" /> PDF
                    </Button>
                  )}
                </div>
                {!data.progress?.length ? (
                  <div className="bg-white rounded-2xl p-6 text-center border border-border/30">
                    <BookOpen className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No progress reports yet</p>
                  </div>
                ) : (
                  data.progress.map((r, i) => (
                    <div key={i} className="bg-white rounded-2xl p-4 border border-border/30 shadow-soft space-y-2">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="text-xs">{new Date(r.date).toLocaleDateString()}</Badge>
                        <RatingStars value={r.rating} />
                      </div>
                      <p className="font-semibold text-sm text-foreground">{r.area}</p>
                      {r.notes && <p className="text-xs text-muted-foreground">{r.notes}</p>}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Attendance */}
            {activeSection === 'attendance' && (
              <div className="space-y-3" data-testid="mobile-parent-attendance">
                <div className="flex items-center justify-between">
                  <h3 className="font-fredoka font-semibold text-foreground">Attendance</h3>
                  {data.attendance?.length > 0 && (
                    <Button variant="ghost" size="sm" onClick={exportAttendancePDF} className="text-xs" data-testid="mobile-export-attendance">
                      <Download className="w-3.5 h-3.5 mr-1" /> PDF
                    </Button>
                  )}
                </div>
                {!data.attendance?.length ? (
                  <div className="bg-white rounded-2xl p-6 text-center border border-border/30">
                    <ClipboardCheck className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No attendance records yet</p>
                  </div>
                ) : (
                  data.attendance.map((a, i) => (
                    <div key={i} className="bg-white rounded-2xl p-4 border border-border/30 shadow-soft flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-foreground">{new Date(a.date).toLocaleDateString()}</p>
                        {a.notes && <p className="text-xs text-muted-foreground mt-0.5">{a.notes}</p>}
                      </div>
                      <Badge className={`${statusColors[a.status] || ''} text-xs capitalize`}>{a.status}</Badge>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Photos */}
            {activeSection === 'photos' && (
              <div className="space-y-3" data-testid="mobile-parent-photos">
                <h3 className="font-fredoka font-semibold text-foreground">Photos</h3>
                {!photos.length ? (
                  <div className="bg-white rounded-2xl p-6 text-center border border-border/30">
                    <Image className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No photos yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {photos.map((p, i) => (
                      <div key={i} className="aspect-square rounded-2xl overflow-hidden cursor-pointer shadow-soft" onClick={() => setLightboxPhoto(p)}>
                        <img src={`${FILES_BASE}/${p.path}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                      </div>
                    ))}
                  </div>
                )}
                {/* Lightbox */}
                <AnimatePresence>
                  {lightboxPhoto && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightboxPhoto(null)}>
                      <button onClick={() => setLightboxPhoto(null)} className="absolute top-4 right-4 text-white/70 hover:text-white"><X className="w-6 h-6" /></button>
                      <img src={`${FILES_BASE}/${lightboxPhoto.path}`} alt="" className="max-w-full max-h-[85vh] rounded-xl object-contain" onClick={(e) => e.stopPropagation()} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Chat */}
            {activeSection === 'chat' && (
              <div className="flex flex-col" style={{ height: 'calc(100vh - 240px)' }} data-testid="mobile-parent-chat">
                <h3 className="font-fredoka font-semibold text-foreground mb-3">Chat with Admin</h3>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto space-y-2 pr-1 pb-2 no-scrollbar">
                  {!chatMessages.length ? (
                    <div className="bg-white rounded-2xl p-6 text-center border border-border/30 mt-4">
                      <MessageSquare className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">No messages yet. Say hi!</p>
                    </div>
                  ) : (
                    chatMessages.map((m) => (
                      <div key={m.id} className={`flex ${m.sender === 'parent' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm ${
                          m.sender === 'parent'
                            ? 'bg-secondary text-white rounded-br-md'
                            : 'bg-white border border-border/30 text-foreground rounded-bl-md shadow-soft'
                        }`} data-testid={`chat-msg-${m.id}`}>
                          {m.photo_path && (
                            <img src={`${FILES_BASE}/${m.photo_path}`} alt="Photo" className="w-full max-w-[200px] rounded-xl mb-1.5 cursor-pointer" onClick={() => setLightboxPhoto({ path: m.photo_path })} loading="lazy" />
                          )}
                          {m.message && <p className="leading-relaxed">{m.message}</p>}
                          <p className={`text-[10px] mt-1 ${m.sender === 'parent' ? 'text-white/60' : 'text-muted-foreground/60'}`}>
                            {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Input + Photo */}
                <div className="flex gap-2 pt-3 border-t border-border/20 mt-2">
                  <input
                    type="file"
                    accept="image/*"
                    ref={photoInputRef}
                    className="hidden"
                    onChange={(e) => { if (e.target.files?.[0]) sendPhoto(e.target.files[0]); e.target.value = ''; }}
                    data-testid="mobile-chat-photo-input"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="h-10 w-10 rounded-xl flex-shrink-0 text-muted-foreground hover:text-secondary"
                    data-testid="mobile-chat-photo-btn"
                  >
                    <Camera className={`w-5 h-5 ${uploadingPhoto ? 'animate-pulse' : ''}`} />
                  </Button>
                  <Input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                    placeholder="Type a message..."
                    className="rounded-xl h-10 text-sm flex-1"
                    disabled={sendingChat}
                    data-testid="mobile-chat-input"
                  />
                  <Button
                    onClick={sendChat}
                    disabled={sendingChat || !chatInput.trim()}
                    size="icon"
                    className="bg-secondary hover:bg-secondary/90 text-white rounded-xl h-10 w-10 flex-shrink-0"
                    data-testid="mobile-chat-send"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Update Info */}
            {activeSection === 'info' && (
              <div className="space-y-4" data-testid="mobile-parent-info">
                <h3 className="font-fredoka font-semibold text-foreground">Update Information</h3>
                <div className="bg-white rounded-2xl p-4 border border-border/30 space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Phone</Label>
                    <Input value={editInfo.phone || ''} onChange={(e) => setEditInfo(prev => ({ ...prev, phone: e.target.value }))} className="rounded-xl h-10 text-sm" data-testid="mobile-info-phone" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Address</Label>
                    <Input value={editInfo.address || ''} onChange={(e) => setEditInfo(prev => ({ ...prev, address: e.target.value }))} className="rounded-xl h-10 text-sm" data-testid="mobile-info-address" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Emergency Contact Name</Label>
                    <Input value={editInfo.emergency_contact_name || ''} onChange={(e) => setEditInfo(prev => ({ ...prev, emergency_contact_name: e.target.value }))} className="rounded-xl h-10 text-sm" data-testid="mobile-info-emerg-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Emergency Contact Phone</Label>
                    <Input value={editInfo.emergency_contact_phone || ''} onChange={(e) => setEditInfo(prev => ({ ...prev, emergency_contact_phone: e.target.value }))} className="rounded-xl h-10 text-sm" data-testid="mobile-info-emerg-phone" />
                  </div>
                  <Button onClick={saveInfo} disabled={savingInfo} className="w-full bg-secondary hover:bg-secondary/90 text-white rounded-xl" data-testid="mobile-info-save">
                    <CheckCircle2 className="w-4 h-4 mr-2" /> {savingInfo ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            )}

          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default MobileParent;
